import clsx from "clsx";
import {
  Database,
  GraduationCap,
  ImageIcon,
  LogOut,
  Rocket,
} from "lucide-react";
import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useFullDocument } from "../../hooks/useFullDocument";
import { useSchedule } from "../../hooks/useSchedule";
import { useUser } from "../../hooks/useUser";
import { cn } from "../../lib/utils";
import { removeAuthToken, removeGoogleAccessToken } from "../../services/auth";
import { Button } from "../ui/button";
import { Toaster } from "../ui/toaster";
import { TooltipProvider } from "../ui/tooltip";

interface LayoutProps {
  // children: React.ReactNode;
  onLogout: () => void;
}

const SidebarMenuItem: React.FC<{
  icon: JSX.Element;
  name: string;
  pathTo: string;
}> = ({ icon, name, pathTo }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  return (
    <li
      role="button"
      className={cn(
        "flex items-center gap-3 mb-4 px-2 py-2 rounded-md hover:bg-white hover:text-[#0D33F1] cursor-pointer transition-all max-lg:justify-center",
        path === "/" && name === "Presentations"
          ? "text-[#0D33F1] bg-white"
          : ""
      )}
      onClick={() => navigate(pathTo)}
    >
      {icon}
      <p className="max-lg:hidden">{name}</p>
    </li>
  );
};

const Layout: React.FC<LayoutProps> = ({ onLogout }) => {
  const location = useLocation();
  let path = location.pathname;

  const userQuery = useUser();

  // console.log(userQuery);

  const isDocumentPage = path.includes("/document/");
  const isSchedulingPage = path.includes("/scheduling/");
  const documentId = isDocumentPage ? path.split("/")[2] : "";
  const scheduleId = isSchedulingPage ? path.split("/")[2] : "";
  const fullDocument = useFullDocument(documentId || "");
  const schedule = useSchedule(scheduleId || "");

  if (isDocumentPage) {
    path = "/document";
  } else if (isSchedulingPage) {
    path = "/scheduling";
  }

  const HeaderTitle = () => {
    switch (path) {
      case "/":
        return (
          <div className="flex items-center gap-3">
            <ImageIcon size={24} />
            <p className="text-2xl">Presentation Configurations</p>
          </div>
        );
      case "/document":
        return (
          <div className="flex items-center gap-3">
            <ImageIcon size={24} />
            <p className={clsx(documentId ? "text-zinc-500" : "", "text-2xl")}>
              Presentations{" "}
              <span className="text-zinc-700">
                {fullDocument.loading ? "" : "/ " + fullDocument.document.name}
              </span>
            </p>
          </div>
        );
      default:
        return <>Default</>;
    }
  };

  return (
    <div className="w-full h-full flex items-start justify-end text-zinc-700">
      <div className="sidebar w-[350px] h-full flex flex-col bg-[#F9FAFB] fixed top-0 left-0 max-lg:w-[100px] transition-all">
        <div className="px-5 h-[80px] flex items-center gap-3 justify-start max-lg:justify-center border-b">
          <img className="w-8 h-8" src="/logo.svg" alt="logo" />
          <p className="text-lg font-medium max-lg:hidden">FlashDocs</p>
        </div>

        {/* <div className="px-5 py-5">
          {userQuery?.user?.organization_id && (
            <>{userQuery?.user?.organization_id}</>
          )}
        </div> */}

        <div className="px-5 py-3">
          <ul>
            <SidebarMenuItem
              pathTo="/"
              icon={<ImageIcon size={20} />}
              name="Presentations"
            />
          </ul>
        </div>

        <div className="flex-1"></div>

        <div className="px-5 py-8 flex items-center justify-between">
          <div className="max-lg:hidden  px-2 py-1 rounded-full text-black">
            {userQuery?.user?.organization_id && (
              <p className="gradient-2">{userQuery.user.organization_id}</p>
            )}
          </div>

          <Button
            variant={"ghost"}
            className="hover:text-red-500 hover:bg-red-100"
            onClick={() => {
              removeAuthToken();
              removeGoogleAccessToken();
              // removeGoogleRefreshToken();
              // removeUserEmail();
              onLogout();

              window.location.reload();
            }}
          >
            <LogOut size={20} />
          </Button>
        </div>
      </div>
      <div
        // style={{ width: "calc(100% - 350px)" }}
        className="h-full flex flex-col items-start justify-start w-[calc(100%-350px)] max-lg:w-[calc(100%-100px)] transition-all"
      >
        <div className="w-full h-[80px] border-b flex items-center px-10 flex-shrink-0">
          <HeaderTitle />
        </div>
        <main className="w-full">
          <TooltipProvider>
            <Outlet />
          </TooltipProvider>
        </main>
      </div>

      <Toaster />
    </div>
  );
};

export default Layout;
